import CloseIcon from "@mui/icons-material/Close";
import { Grid, IconButton, useTheme } from "@mui/material";
import { SpotlightSparkle } from "components/Signals/BaseObjects/ChartCard/Spotlight";
import { AcxGradientBorderContainer } from "components/UI/AcxGradientBorderContainer";
import { AcxGradientTypography } from "components/UI/AcxGradientTypography";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import ContextStore from "stores/ContextStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { useStore } from "utils/useStore";

// SmartBanner is responsible for asking for the smart banner content to be built
// and then renders that content if it exists.

type SmartBannerProps = {
    title: string;
};

export const SmartBanner = observer((props: SmartBannerProps) => {
    const theme = useTheme();
    const contextStore = useStore(ContextStore);
    const valuesStore = useStore(ApplicationFilterValuesStore);
    const bannerContent = contextStore.bannerContent;

    // Module names come from a list the filter value store loads once. The banner is built a single
    // time, so building while that list is still in flight would leave every module in the sentence
    // called "the module score" for as long as the banner is up.
    const needsModuleNames =
        (contextStore.context?.filters?.moduleScores?.length ?? 0) > 0;
    const isLoadingModuleNames = valuesStore.getTaskLoading(
        ApplicationFilterValuesStore.Tasks.LOAD_LICENSED_HIERARCHY_MODULES,
    );

    useEffect(() => {
        if (bannerContent) return; // Already have banner content
        if (needsModuleNames && isLoadingModuleNames) return;

        contextStore.buildSmartBanner();
    }, [
        contextStore,
        bannerContent,
        contextStore.context,
        needsModuleNames,
        isLoadingModuleNames,
    ]);

    if (!bannerContent) return null;

    return (
        <AcxGradientBorderContainer backgroundColor={theme.palette.white.main}>
            <Grid
                container
                item
                direction={"column"}
                padding={1}
                paddingRight={0.25}
                gap={1}
            >
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                    gap={1}
                >
                    <Grid
                        container
                        item
                        alignItems="center"
                        gap={0.5}
                        wrap="nowrap"
                    >
                        <AcxGradientTypography
                            sx={{
                                color: theme.palette.secondary.dark,
                                textTransform: "uppercase",
                                letterSpacing: "2px",
                                lineHeight: 2,
                            }}
                        >
                            {props.title}
                        </AcxGradientTypography>
                        <SpotlightSparkle />
                    </Grid>

                    <Grid item>
                        <IconButton
                            onClick={() => {
                                contextStore.clearBannerContent();
                            }}
                            size="small"
                            id="close-smart-banner-button"
                            sx={{
                                padding: 0,
                                width: 20,
                                height: 20,
                                minWidth: 20,
                                minHeight: 20,
                            }}
                        >
                            <CloseIcon
                                style={{
                                    fontSize: 20,
                                    color: theme.palette.gray[400],
                                }}
                            />
                        </IconButton>
                    </Grid>
                </Grid>

                <Grid
                    container
                    alignItems="center"
                    sx={{ paddingBottom: 1 }}
                    data-testid="smart-banner-content"
                >
                    {bannerContent}
                </Grid>
            </Grid>
        </AcxGradientBorderContainer>
    );
});
